import { Link, useLocation } from "react-router-dom";
import ItemListElem from "../core/ItemListElem.tsx";
import { memo, useContext, useLayoutEffect, useRef } from "react";
import ItemCard from "../core/ItemCard";
import { CompactContext } from "../../../context/context.compact";
import { useItems } from "../../../fetch/fetch.item";
import ErrMessage from "../../core/ErrMessage";
import { IconZoomExclamation } from "@tabler/icons-react";
import { useActiveParams } from "../../../hooks/useActiveParams.ts";
import Alert from "../../core/Alert.tsx";
import { SubmitProvider } from "../../../context/context.submit.tsx";
import { LOADING_CIRCLE } from "../../core/loading/LoadingCircle.tsx";
import { ROUTE_PRODUCT } from "../../../const/routes.ts";

const renderItemCard = (id: string) => (
  <Link key={id} to={ROUTE_PRODUCT(id)}>
    <ItemCard itemId={id} />
  </Link>
);

const renderItemList = (id: string) => (
  <Link key={id} to={ROUTE_PRODUCT(id)}>
    <ItemListElem id={id} />
  </Link>
);
const ItemGrid = memo(function ItemGrid({ itemIds }: { itemIds: string[] }) {
  return (
    <SubmitProvider>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 w-full auto-rows-fr">
        {itemIds.map(renderItemCard)}
      </div>
    </SubmitProvider>
  );
});

const ItemList = memo(function ItemList({ itemIds }: { itemIds: string[] }) {
  return (
    <SubmitProvider>
      <ul className="grid gap-1.5 w-full">{itemIds.map(renderItemList)}</ul>
    </SubmitProvider>
  );
});
const ItemLayoutContainer = ({
  userId,
  fixedPageSize,
}: {
  userId?: string;
  fixedPageSize?: number;
}) => {
  const params = useActiveParams(userId, fixedPageSize);
  const { compact } = useContext(CompactContext);
  const { itemIds, isLoading, isError } = useItems(params);

  const { key } = useLocation();
  const scrollRestored = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (isLoading) return;
    if (scrollRestored.current === key) return;
    scrollRestored.current = key;

    const saved = sessionStorage.getItem(`scroll:${key}`);
    if (saved) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: Number(saved), behavior: "instant" });
      });
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [isLoading, key]);

  useLayoutEffect(() => {
    return () => {
      sessionStorage.setItem(`scroll:${key}`, String(window.scrollY));
    };
  }, [key]);

  if (isError) return <ErrMessage message={isError.message} />;
  return (
    <div className="h-full">
      <div
        className={`absolute right-3 bottom-3 toast safe-bottom-with-dock animate-bounce ${!isLoading && "hidden"}`}
      >
        <Alert icon={LOADING_CIRCLE} text="Lade..." />
      </div>
      <div>
        <div
          className={`badge badge-xl h-fit ${((!isLoading && itemIds.length > 0) || isLoading) && "hidden"}`}
        >
          <IconZoomExclamation />
          <p>Nicht&#39;s gefunden.</p>
        </div>
      </div>

      <div
        className={`${isLoading ? "opacity-0 lg:opacity-50 saturate-0 pointer-events-none" : "opacity-100 saturate-100 pointer-events-auto"} transition-all duration-500`}
      >
        {compact ? (
          <ItemList itemIds={itemIds} />
        ) : (
          <ItemGrid itemIds={itemIds} />
        )}
      </div>
    </div>
  );
};

export default memo(ItemLayoutContainer);
